import { useTranslation } from '@/lib/i18n';
import type { FundingStatistics, Money } from '@/lib/api-admin/types';

const formatMoney = (money: Money | undefined | null): string => {
  if (!money) return '—';
  return `${new Intl.NumberFormat('ko-KR').format(money.amount)} ${money.currencyCode}`;
};

const formatCount = (value: number | undefined | null): string =>
  new Intl.NumberFormat('ko-KR').format(value ?? 0);

const formatRate = (value: number | undefined | null): string =>
  `${(value ?? 0).toFixed(1)}%`;

interface KpiTileProps {
  label: string;
  value: string;
  tone?: 'default' | 'success' | 'warn' | 'danger';
}

const TONE_FG: Record<NonNullable<KpiTileProps['tone']>, string> = {
  default: 'var(--content-strong)',
  success: 'var(--status-success-fg)',
  warn: 'var(--status-warn-fg)',
  danger: 'var(--status-danger-fg)',
};

const KpiTile = ({ label, value, tone = 'default' }: KpiTileProps) => (
  <div
    className="flex flex-col gap-1 rounded-md p-3"
    style={{ background: 'var(--surface-1)' }}
  >
    <span className="text-xs text-[color:var(--content-muted)]">{label}</span>
    <span
      className="text-base font-semibold"
      style={{ color: TONE_FG[tone] }}
      data-testid="kpi-value"
    >
      {value}
    </span>
  </div>
);

export interface FundingStatsCardProps {
  stats: FundingStatistics;
}

export const FundingStatsCard = ({ stats }: FundingStatsCardProps) => {
  const { t } = useTranslation('funding');
  const rewardBreakdown = stats.rewardBreakdown ?? [];
  return (
    <section
      aria-labelledby="funding-stats-heading"
      className="rounded-lg p-5"
      style={{
        background: 'var(--surface-raised)',
        boxShadow: 'var(--shadow-card)',
      }}
      data-testid="funding-stats-card"
    >
      <h3
        id="funding-stats-heading"
        className="mb-3 text-sm font-semibold text-[color:var(--content-strong)]"
      >
        {t('funding.detail.tabStatistics')}
      </h3>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <KpiTile
          label={t('funding.stats.totalPledge')}
          value={formatCount(stats.totalPledgeCount)}
        />
        <KpiTile
          label={t('funding.stats.totalPaid')}
          value={formatCount(stats.totalPaidCount)}
          tone="success"
        />
        <KpiTile
          label={t('funding.stats.totalCancelled')}
          value={formatCount(stats.totalCancelledCount)}
        />
        <KpiTile
          label={t('funding.stats.totalRefunded')}
          value={formatCount(stats.totalRefundedCount)}
          tone="warn"
        />
        <KpiTile
          label={t('funding.stats.totalAmount')}
          value={formatMoney(stats.totalAmount)}
          tone="success"
        />
        <KpiTile
          label={t('funding.stats.achievementRate')}
          value={formatRate(stats.achievementRate)}
        />
        <KpiTile
          label={t('funding.stats.avgPledgeAmount')}
          value={formatMoney(stats.avgPledgeAmount)}
        />
      </div>

      {rewardBreakdown.length > 0 ? (
        <div className="mt-5">
          <h4 className="mb-2 text-xs font-semibold text-[color:var(--content-strong)]">
            {t('funding.stats.rewardBreakdown')}
          </h4>
          <ul className="flex flex-col gap-1.5">
            {rewardBreakdown.map((item) => (
              <li
                key={item.rewardId}
                className="flex items-center justify-between rounded-md px-3 py-2 text-sm"
                style={{ background: 'var(--surface-1)' }}
              >
                <span className="text-[color:var(--content-strong)]">{item.tierName}</span>
                <span className="text-xs text-[color:var(--content-muted)]">
                  {t('funding.stats.selected', { count: item.selectedQuantity })}
                  {' · '}
                  {item.remainingQuantity == null
                    ? t('funding.stats.unlimited')
                    : t('funding.stats.remaining', { count: item.remainingQuantity })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
};
